/**
* Spreadsheet Formulas Parser and Evaluator
*
* Supports:
* - Basic math operations: +, -, *, /, ^
* - Cell references: A1, B2, etc.
* - Range references: A1:A5, B1:D3
* - Functions: SUM, AVG, COUNT, MIN, MAX, IF
*/

import { logger } from '@/utils/logger';

export type CellValue = string | number | null;
export type GridData = Record<string, CellValue>;

// Column letter to index (A=0, B=1, ..., Z=25, AA=26, etc.)
export function columnToIndex(col: string): number {
  let result = 0;
  for (let i = 0; i < col.length; i++) {
  result = result * 26 + col.charCodeAt(i) - 64;
  }
  return result - 1;
}

// Index to column letter
export function indexToColumn(index: number): string {
  let result = '';
  index++;
  while (index > 0) {
  const remainder = (index - 1) % 26;
  result = String.fromCharCode(65 + remainder) + result;
  index = Math.floor((index - 1) / 26);
  }
  return result;
}

// Parse cell reference (e.g., "A1" -> { col: 0, row: 0 })
export function parseCellRef(ref: string): { col: number; row: number } | null {
  const match = ref.match(/^([A-Z]+)(\d+)$/i);
  if (!match) return null;

  const col = columnToIndex(match[1].toUpperCase());
  const row = parseInt(match[2], 10) - 1;

  return { col, row };
}

// Generate cell ID from row and column indices
export function getCellId(row: number, col: number): string {
  return `${row}-${col}`;
}

// Parse cell ID to get row and column
export function parseCellId(cellId: string): { row: number; col: number } | null {
  const match = cellId.match(/^(\d+)-(\d+)$/);
  if (!match) return null;
  return { row: parseInt(match[1], 10), col: parseInt(match[2], 10) };
}

// Get cell reference string (e.g., "A1")
export function getCellRef(row: number, col: number): string {
  return `${indexToColumn(col)}${row + 1}`;
}

// Expand a range reference to individual cell references
export function expandRange(startRef: string, endRef: string): string[] {
  const start = parseCellRef(startRef);
  const end = parseCellRef(endRef);

  if (!start || !end) return [];

  const cells: string[] = [];
  const minCol = Math.min(start.col, end.col);
  const maxCol = Math.max(start.col, end.col);
  const minRow = Math.min(start.row, end.row);
  const maxRow = Math.max(start.row, end.row);

  for (let row = minRow; row <= maxRow; row++) {
  for (let col = minCol; col <= maxCol; col++) {
      cells.push(getCellRef(row, col));
  }
  }

  return cells;
}

// Get numeric value from cell
function getNumericValue(value: CellValue): number {
  if (value === null || value === '') return 0;
  if (typeof value === 'number') return value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
}

// Valori di errore riconoscibili: si propagano invece di diventare zero
const ERROR_VALUES = ['#CIRCULAR!', '#NAME?', '#ERROR!'];

function isErrorValue(value: CellValue): boolean {
  return typeof value === 'string' && ERROR_VALUES.includes(value);
}

function firstError(values: CellValue[]): CellValue | null {
  return values.find(isErrorValue) ?? null;
}

/**
 * Valore di una cella, con le formule già risolte.
 *
 * Una cella può contenere una formula che ne referenzia un'altra che a sua
 * volta è una formula: senza questa risoluzione ricorsiva `=SUM(E2:E5)` con
 * E2..E5 formule sommava le stringhe "=C2*D2" e restituiva zero.
 *
 * `visiting` tiene le celle in corso di valutazione lungo la catena corrente:
 * se ne ricompare una, il riferimento è circolare e si ferma lì.
 */
function resolveCellValue(cellId: string, gridData: GridData, visiting: Set<string>): CellValue {
  const raw = gridData[cellId] ?? null;
  if (!isFormula(raw)) return raw;

  if (visiting.has(cellId)) return '#CIRCULAR!';

  visiting.add(cellId);
  try {
    return evaluateFormula(raw as string, gridData, visiting);
  } finally {
    visiting.delete(cellId);
  }
}

// Get cell value by reference
function getCellValueByRef(ref: string, gridData: GridData, visiting: Set<string>): CellValue {
  const parsed = parseCellRef(ref);
  if (!parsed) return null;

  return resolveCellValue(getCellId(parsed.row, parsed.col), gridData, visiting);
}

// Get multiple cell values from a range
function getRangeValues(refs: string[], gridData: GridData, visiting: Set<string>): CellValue[] {
  return refs.map(ref => getCellValueByRef(ref, gridData, visiting));
}

// Built-in functions
const FUNCTIONS: Record<string, (args: CellValue[]) => CellValue> = {
  SUM: (args) => {
  return args.reduce((sum: number, val) => sum + getNumericValue(val), 0);
  },

  AVG: (args) => {
  const numericArgs = args.filter(v => v !== null && v !== '');
  if (numericArgs.length === 0) return 0;
  const sum = numericArgs.reduce((s: number, val) => s + getNumericValue(val), 0);
  return sum / numericArgs.length;
  },

  AVERAGE: (args) => FUNCTIONS.AVG(args),

  COUNT: (args) => {
  return args.filter(v => v !== null && v !== '' && !isNaN(getNumericValue(v))).length;
  },

  COUNTA: (args) => {
  return args.filter(v => v !== null && v !== '').length;
  },

  MIN: (args) => {
  const numericArgs = args.filter(v => v !== null && v !== '').map(getNumericValue);
  if (numericArgs.length === 0) return 0;
  return Math.min(...numericArgs);
  },

  MAX: (args) => {
  const numericArgs = args.filter(v => v !== null && v !== '').map(getNumericValue);
  if (numericArgs.length === 0) return 0;
  return Math.max(...numericArgs);
  },

  IF: (args) => {
  const [condition, trueValue, falseValue] = args;
  return getNumericValue(condition) !== 0 ? (trueValue ?? 0) : (falseValue ?? 0);
  },

  ABS: (args) => Math.abs(getNumericValue(args[0])),

  ROUND: (args) => {
  const value = getNumericValue(args[0]);
  const decimals = getNumericValue(args[1] ?? 0);
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
  },

  SQRT: (args) => Math.sqrt(getNumericValue(args[0])),

  POW: (args) => Math.pow(getNumericValue(args[0]), getNumericValue(args[1] ?? 1)),

  LEN: (args) => String(args[0] ?? '').length,

  UPPER: (args) => String(args[0] ?? '').toUpperCase(),

  LOWER: (args) => String(args[0] ?? '').toLowerCase(),

  CONCAT: (args) => args.map(a => String(a ?? '')).join(''),
};

// Tokenize formula
interface Token {
  type: 'number' | 'string' | 'cell' | 'range' | 'function' | 'operator' | 'paren' | 'comma';
  value: string;
}

function tokenize(formula: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < formula.length) {
  const char = formula[i];

  // Skip whitespace
  if (/\s/.test(char)) {
      i++;
      continue;
  }

  // Numbers
  if (/\d/.test(char) || (char === '.' && /\d/.test(formula[i + 1]))) {
      let num = '';
      while (i < formula.length && (/\d/.test(formula[i]) || formula[i] === '.')) {
    num += formula[i];
    i++;
      }
      tokens.push({ type: 'number', value: num });
      continue;
  }

  // Strings in quotes
  if (char === '"' || char === "'") {
      const quote = char;
      let str = '';
      i++;
      while (i < formula.length && formula[i] !== quote) {
    str += formula[i];
    i++;
      }
      i++; // Skip closing quote
      tokens.push({ type: 'string', value: str });
      continue;
  }

  // Cell references, ranges, or functions
  if (/[A-Z]/i.test(char)) {
      let word = '';
      while (i < formula.length && /[A-Z0-9]/i.test(formula[i])) {
    word += formula[i];
    i++;
      }

      // Check for range (e.g., A1:B5)
      if (formula[i] === ':') {
    i++; // Skip colon
    let endRef = '';
    while (i < formula.length && /[A-Z0-9]/i.test(formula[i])) {
          endRef += formula[i];
          i++;
    }
    tokens.push({ type: 'range', value: `${word}:${endRef}` });
      } else if (formula[i] === '(') {
    // It's a function
    tokens.push({ type: 'function', value: word.toUpperCase() });
      } else if (/^[A-Z]+\d+$/i.test(word)) {
    // It's a cell reference
    tokens.push({ type: 'cell', value: word.toUpperCase() });
      } else {
    // Treat as string
    tokens.push({ type: 'string', value: word });
      }
      continue;
  }

  // Operators
  if (['+', '-', '*', '/', '^', '>', '<', '=', '!'].includes(char)) {
      // Handle multi-char operators
      let op = char;
      if (i + 1 < formula.length) {
    const next = formula[i + 1];
    if ((char === '>' || char === '<' || char === '!' || char === '=') && next === '=') {
          op += next;
          i++;
    } else if (char === '<' && next === '>') {
          op = '<>';
          i++;
    }
      }
      tokens.push({ type: 'operator', value: op });
      i++;
      continue;
  }

  // Parentheses
  if (char === '(' || char === ')') {
      tokens.push({ type: 'paren', value: char });
      i++;
      continue;
  }

  // Comma
  if (char === ',') {
      tokens.push({ type: 'comma', value: ',' });
      i++;
      continue;
  }

  // Unknown character, skip
  i++;
  }

  return tokens;
}

// Simple expression evaluator
function evaluateExpression(tokens: Token[], gridData: GridData, visiting: Set<string>): CellValue {
  if (tokens.length === 0) return null;

  // Handle single value
  if (tokens.length === 1) {
  const token = tokens[0];
  switch (token.type) {
      case 'number':
    return parseFloat(token.value);
      case 'string':
    return token.value;
      case 'cell':
    return getCellValueByRef(token.value, gridData, visiting);
      case 'range': {
    const [start, end] = token.value.split(':');
    const cells = expandRange(start, end);
    const values = getRangeValues(cells, gridData, visiting);
    return firstError(values) ?? FUNCTIONS.SUM(values); // Default to SUM for bare ranges
      }
      default:
    return null;
  }
  }

  // Handle function calls
  if (tokens[0].type === 'function') {
  const funcName = tokens[0].value;
  const func = FUNCTIONS[funcName];

  if (!func) {
      return `#NAME?`; // Unknown function
  }

  // Parse arguments (between parentheses)
  const args: CellValue[] = [];
  let depth = 0;
  let currentArg: Token[] = [];

  for (let i = 1; i < tokens.length; i++) {
      const token = tokens[i];

      if (token.type === 'paren' && token.value === '(') {
    if (depth > 0) currentArg.push(token);
    depth++;
      } else if (token.type === 'paren' && token.value === ')') {
    depth--;
    if (depth === 0) {
          if (currentArg.length > 0) {
      args.push(evaluateExpression(currentArg, gridData, visiting));
          }
          currentArg = [];
    } else {
          currentArg.push(token);
    }
      } else if (token.type === 'comma' && depth === 1) {
    if (currentArg.length > 0) {
          args.push(evaluateExpression(currentArg, gridData, visiting));
    }
    currentArg = [];
      } else if (depth > 0) {
    // Handle ranges specially
    if (token.type === 'range') {
          const [start, end] = token.value.split(':');
          const cells = expandRange(start, end);
          const values = getRangeValues(cells, gridData, visiting);
          args.push(...values);
    } else {
          currentArg.push(token);
    }
      }
  }

  return firstError(args) ?? func(args);
  }

  // Handle basic math expressions (simple left-to-right evaluation)
  let result = 0;
  let operator = '+';

  for (const token of tokens) {
  if (token.type === 'operator') {
      operator = token.value;
  } else {
      let value = 0;

      if (token.type === 'number') {
    value = parseFloat(token.value);
      } else if (token.type === 'cell') {
    const cellValue = getCellValueByRef(token.value, gridData, visiting);
    if (isErrorValue(cellValue)) return cellValue;
    value = getNumericValue(cellValue);
      }

      switch (operator) {
    case '+':
          result += value;
          break;
    case '-':
          result -= value;
          break;
    case '*':
          result *= value;
          break;
    case '/':
          result = value !== 0 ? result / value : NaN;
          break;
    case '^':
          result = Math.pow(result, value);
          break;
      }
  }
  }

  return result;
}

/**
* Evaluate a formula string
* @param formula The formula string (with or without leading =)
* @param gridData The grid data object with cell values
* @param visiting Celle già in valutazione lungo questa catena, per fermare
*                 i riferimenti circolari. Da lasciare al valore di default.
* @returns The evaluated result or an error string
*/
export function evaluateFormula(
  formula: string,
  gridData: GridData,
  visiting: Set<string> = new Set(),
): CellValue {
  // Remove leading = if present
  const cleanFormula = formula.startsWith('=') ? formula.slice(1).trim() : formula.trim();

  if (!cleanFormula) return null;

  try {
  const tokens = tokenize(cleanFormula);
  return evaluateExpression(tokens, gridData, visiting);
  } catch (error) {
  logger.error('[Spreadsheet] Formula evaluation error:', error);
  return '#ERROR!';
  }
}

/**
* Check if a value is a formula (starts with =)
*/
export function isFormula(value: CellValue): boolean {
  return typeof value === 'string' && value.startsWith('=');
}

/**
* Get the display value for a cell (evaluates formulas)
*/
export function getDisplayValue(cellValue: CellValue, gridData: GridData): string {
  if (cellValue === null || cellValue === '') return '';

  if (isFormula(cellValue)) {
  const result = evaluateFormula(cellValue as string, gridData);
  if (result === null) return '';
  if (typeof result === 'number') {
      // Format number nicely
      if (Number.isInteger(result)) return result.toString();
      return result.toFixed(2);
  }
  return String(result);
  }

  return String(cellValue);
}
